import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@swyftx/aviary/atoms/Button';
import { Card } from '@swyftx/aviary/atoms/Card';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { ModalProps } from '@swyftx/aviary/atoms/Modal/Modal.styles';
import { ScrollView } from '@swyftx/aviary/atoms/ScrollView';
import { InformationCircle, Loading } from '@swyftx/aviary/icons/outlined';
import { MarginOrderDirection } from '@swyftx/margin/build/types';

import { useMarginInstruments } from 'src/lib/margin/hooks/instruments';
import { useMarginTradePanelStore } from 'src/lib/margin/hooks/trade';

import {
  MarginTradePanelBuySellTabs,
  MarginTradePanelContent,
  MarginTradePanelReviewFooter,
  MarginTradePanelTypeTab,
} from './components';
import { MarginTradeTypeTooltipModal } from '../../TooltipModals';

type Props = {
  initialTradeSide?: MarginOrderDirection;
  modalPosition?: ModalProps['position'];
};

const MarginTradePanel: React.FC<Props> = ({ initialTradeSide, modalPosition = 'dynamic' }) => {
  const { setTradeSide, tradeSide, setTradeType, tradeType } = useMarginTradePanelStore();
  const { selectedMarketAsset } = useMarginInstruments();
  const { t } = useTranslation('margin');

  useEffect(() => {
    if (initialTradeSide !== undefined) setTradeSide(initialTradeSide);
  }, [initialTradeSide, setTradeSide]);

  return (
    <Card className='relative h-full w-full rounded-[12px] border-none md:border-solid'>
      <FlexLayout
        alignItems='start'
        justifyContent='space-between'
        direction='column'
        className='relative h-full w-full'
      >
        <ScrollView className='w-full'>
          <FlexLayout alignItems='start' justifyContent='start' direction='column' className='relative h-full w-full'>
            <MarginTradePanelBuySellTabs tradeSide={tradeSide} setSide={setTradeSide} />
            <FlexLayout
              className='w-full border-b border-color-border-main pr-12'
              alignItems='center'
              justifyContent='space-between'
            >
              <MarginTradePanelTypeTab tradeType={tradeType} setTradeType={setTradeType} />
              <MarginTradeTypeTooltipModal defaultValue='opening' marginDirection={tradeSide} marginType={tradeType}>
                <Button
                  layout='icon'
                  tooltip={t('tradePanel.tooltips.orderTypes')}
                  size='sm'
                  leadingIcon={<InformationCircle className='h-full w-full text-color-text-primary' />}
                  variant='ghost'
                />
              </MarginTradeTypeTooltipModal>
            </FlexLayout>
            <FlexLayout className='h-full w-full' alignItems='center' justifyContent='center'>
              {selectedMarketAsset ? (
                <MarginTradePanelContent tradeType={tradeType} tradeSide={tradeSide} asset={selectedMarketAsset} />
              ) : (
                <Loading className='mt-48 animate-spin' />
              )}
            </FlexLayout>
          </FlexLayout>
        </ScrollView>
        <MarginTradePanelReviewFooter tradeSide={tradeSide} modalPosition={modalPosition} />
      </FlexLayout>
    </Card>
  );
};

export { MarginTradePanel };
